import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  pullDistance?: number;
  threshold?: number;
  className?: string;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  pullDistance = 80,
  threshold = 60,
  className = ''
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const [canRefresh, setCanRefresh] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const isPulling = useRef<boolean>(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (container.scrollTop === 0 && !isRefreshing) {
        startY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;

      if (diff > 0) {
        e.preventDefault();
        const progress = Math.min(diff / pullDistance, 1);
        setPullProgress(progress);
        setCanRefresh(progress >= threshold / pullDistance);
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling.current) return;

      if (canRefresh && !isRefreshing) {
        setIsRefreshing(true);
        setPullProgress(0);
        setCanRefresh(false);

        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
        }
      } else {
        setPullProgress(0);
        setCanRefresh(false);
      }

      isPulling.current = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canRefresh, isRefreshing, pullDistance, threshold, onRefresh]);

  return (
    <div className={`relative ${className}`}>
      {/* Pull indicator */}
      <div
        className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center transition-transform duration-200 ease-out"
        style={{
          transform: `translateY(${Math.max(-100, pullProgress * 100 - 100)}%)`,
          height: '60px'
        }}
      >
        <div className="flex items-center space-x-3 text-gray-600">
          {isRefreshing ? (
            <>
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium">Refreshing...</span>
            </>
          ) : (
            <>
              <div
                className={`transform transition-transform duration-200 ${
                  canRefresh ? 'rotate-180 text-blue-600' : 'text-gray-400'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <span className={`text-sm font-medium ${canRefresh ? 'text-blue-600' : 'text-gray-400'}`}>
                {canRefresh ? 'Release to refresh' : 'Pull to refresh'}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Content container */}
      <div
        ref={containerRef}
        className="overflow-y-auto"
        style={{
          transform: `translateY(${pullProgress * 60}px)`,
          transition: isRefreshing ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        {/* Spacer for pull indicator */}
        <div className="h-0" />

        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
